import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { createApiCall } from 'services'

/**
 * VERA-931 — the HQ -> store custody hop.
 *
 * Dispatch OPENS a hop and bills nothing. Receipt CLOSES it and, when the resolved
 * activation trigger is AT_LOCATION, activates the certificate — which is a billable
 * event. The receive response says whether that happened, and callers must surface
 * it rather than assume: "sent to the store" and "you have just been charged" are
 * different facts and the user is entitled to the second one.
 */
export type CustodyEvent = {
  id: string
  certificate_id: string
  company_id: string
  to_address_id: string
  status: 'dispatched' | 'received' | 'cancelled'
  dispatched_at: string
  received_at: string | null
  receipt_method: 'transferred' | 'claimed' | 'scan_claimed' | null
}

export type ReceiveResult = CustodyEvent & {
  /** True when THIS receipt activated the certificate, and therefore billed it. */
  activated: boolean
}

const OPEN_HOPS_KEY = ['custody', 'open']

/**
 * Open hops for the company, oldest dispatch first — the dispatch-age report.
 *
 * This query is the reason hops are never auto-closed. Auto-completing on a timer
 * would activate and bill without evidence, so stock that never arrived is made
 * visible to a human instead.
 */
export const useOpenCustodyHops = (options?: { enabled?: boolean }) => {
  const query = useQuery({
    queryKey: OPEN_HOPS_KEY,
    queryFn: async (): Promise<CustodyEvent[]> => {
      const response = await createApiCall({
        method: 'GET',
        url: '/custody/admin/open',
      })
      return (response.data ?? []) as CustodyEvent[]
    },
    enabled: options?.enabled !== false,
    staleTime: 1000 * 30,
  })

  return {
    hops: query.data ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}

type CustodyMutationOptions = {
  onDispatched?: (event: CustodyEvent) => void
  onReceived?: (result: ReceiveResult) => void
  onError?: (error: unknown) => void
}

export const useCustodyMutations = (options?: CustodyMutationOptions) => {
  const queryClient = useQueryClient()

  const invalidate = async (certificateId?: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: OPEN_HOPS_KEY }),
      queryClient.invalidateQueries({ queryKey: ['certificates'] }),
      certificateId
        ? queryClient.invalidateQueries({
            queryKey: ['certificates', 'detail', certificateId],
          })
        : Promise.resolve(),
    ])
  }

  const dispatchMutation = useMutation({
    mutationFn: async (payload: { certificateId: string; addressId: string }) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/custody/admin/dispatch',
        data: { certificate_id: payload.certificateId, address_id: payload.addressId },
      })
      // 201, not 200 — dispatch CREATES a hop. A 409 here means the certificate is
      // already in transit, which the modal surfaces rather than swallowing.
      if (response.status !== 201) {
        throw response.data ?? new Error('Failed to send to store')
      }
      return response.data as CustodyEvent
    },
    onSuccess: async (event, variables) => {
      await invalidate(variables.certificateId)
      options?.onDispatched?.(event)
    },
    onError: error => options?.onError?.(error),
  })

  const receiveMutation = useMutation({
    mutationFn: async (payload: {
      custodyEventId: string
      certificateId: string
      method: 'transferred' | 'scan_claimed'
    }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/custody/admin/${payload.custodyEventId}/receive`,
        data: { method: payload.method },
      })
      if (response.status !== 200) {
        throw response.data ?? new Error('Failed to confirm receipt')
      }
      return response.data as ReceiveResult
    },
    onSuccess: async (result, variables) => {
      await invalidate(variables.certificateId)
      options?.onReceived?.(result)
    },
    onError: error => options?.onError?.(error),
  })

  return {
    sendToStore: dispatchMutation.mutate,
    confirmReceipt: receiveMutation.mutate,
    isDispatching: dispatchMutation.isPending,
    isReceiving: receiveMutation.isPending,
  }
}

/**
 * VERA-931 — has this certificate already ARRIVED at a location?
 *
 * Reads the admin provenance chain, which gains a store row the moment a hop
 * completes, rather than adding an endpoint for one boolean. `useOpenCustodyHops`
 * cannot answer it: a RECEIVED hop is by definition no longer open.
 *
 * The answer only changes the wording of a confirmation, so it is cheap to be
 * wrong-but-stale and expensive to add machinery — hence the reuse and the generous
 * staleTime.
 */
export const useHasArrivedAtLocation = (
  certificateId: string,
  options?: { enabled?: boolean },
) => {
  const query = useQuery({
    queryKey: ['custody', 'arrived', certificateId],
    queryFn: async (): Promise<boolean> => {
      const response = await createApiCall({
        method: 'GET',
        url: `/certificates/admin/provenance/${certificateId}?limit=100&offset=0`,
      })
      const rows = (response.data?.data ?? []) as Array<{ owner_type?: string }>
      return rows.some(row => row.owner_type === 'store')
    },
    enabled: options?.enabled !== false && Boolean(certificateId),
    staleTime: 1000 * 60,
  })
  return { hasArrived: query.data ?? false, isLoading: query.isLoading }
}
